/**
 * K3 Comparative Notes Section
 *
 * Hanterar jämförelsetal (comparative figures) för K3-noter
 * K3 kap 3.10 - Jämförelsetal är OBLIGATORISKA i noter (till skillnad från K2)
 */

import { useState, useEffect } from "react";
import { History, AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Info } from "lucide-react";
import { formatNumber } from "@/lib/format";
import type { UserInput, AnnualReport } from "@/lib/api";

interface ComparativeNoteData {
  noteId: string;
  noteName: string;
  legalReference: string;
  currentYear: number | null;
  previousYear: number | null;
  requiresComparative: boolean;
  hasComparative: boolean;
  userInputField?: string;
  helpText?: string;
}

interface K3ComparativeNotesSectionProps {
  report: AnnualReport;
  userInput: UserInput;
  previousYearUserInput?: UserInput | null;
  onChange: (comparatives: UserInput["comparativeNotes"]) => void;
}

// Define which notes need comparative figures and how to extract them
const COMPARATIVE_NOTES_CONFIG: Array<{
  noteId: string;
  noteName: string;
  legalReference: string;
  requiresComparative: boolean;
  currentYearPath?: string;
  previousYearPath?: string;
  manualInputField?: string;
  helpText?: string;
}> = [
  {
    noteId: "NOTE_EMPLOYEES_K3",
    noteName: "Medelantal anställda",
    legalReference: "K3 kap 8.24-8.27",
    requiresComparative: true,
    currentYearPath: "averageEmployees",
    previousYearPath: "averageEmployees",
    manualInputField: "previousYearEmployees",
    helpText: "Antal anställda föregående räkenskapsår",
  },
  {
    noteId: "NOTE_REVENUE_BREAKDOWN",
    noteName: "Nettoomsättning",
    legalReference: "K3 kap 8.7",
    requiresComparative: true,
    manualInputField: "previousYearRevenue",
    helpText: "Total nettoomsättning föregående räkenskapsår",
  },
  {
    noteId: "NOTE_FIXED_ASSETS_INTANGIBLE",
    noteName: "Immateriella anläggningstillgångar",
    legalReference: "K3 kap 8.8-8.12",
    requiresComparative: true,
    manualInputField: "previousYearIntangibleAssets",
    helpText: "Bokfört värde vid föregående års utgång",
  },
  {
    noteId: "NOTE_FIXED_ASSETS_BUILDINGS",
    noteName: "Byggnader och mark",
    legalReference: "K3 kap 8.8-8.12",
    requiresComparative: true,
    manualInputField: "previousYearBuildings",
    helpText: "Bokfört värde vid föregående års utgång",
  },
  {
    noteId: "NOTE_FIXED_ASSETS_MACHINERY",
    noteName: "Maskiner och inventarier",
    legalReference: "K3 kap 8.8-8.12",
    requiresComparative: true,
    manualInputField: "previousYearMachinery",
    helpText: "Bokfört värde vid föregående års utgång",
  },
  {
    noteId: "NOTE_INVENTORY",
    noteName: "Varulager",
    legalReference: "K3 kap 13.6-13.7",
    requiresComparative: true,
    manualInputField: "previousYearInventory",
    helpText: "Lagervärde vid föregående års utgång",
  },
  {
    noteId: "NOTE_UNTAXED_RESERVES",
    noteName: "Obeskattade reserver",
    legalReference: "K3 kap 8.16",
    requiresComparative: true,
    manualInputField: "previousYearUntaxedReserves",
    helpText: "Obeskattade reserver vid föregående års utgång",
  },
  {
    noteId: "NOTE_DEFERRED_TAX",
    noteName: "Uppskjuten skatt",
    legalReference: "K3 kap 29.32-29.37",
    requiresComparative: true,
    manualInputField: "previousYearDeferredTax",
    helpText: "Uppskjuten skatteskuld vid föregående års utgång",
  },
  {
    noteId: "NOTE_LONG_TERM_LIABILITIES",
    noteName: "Långfristiga skulder",
    legalReference: "K3 kap 8.17-8.18",
    requiresComparative: true,
    manualInputField: "previousYearLongTermLiabilities",
    helpText: "Långfristiga skulder vid föregående års utgång",
  },
  {
    noteId: "NOTE_PLEDGED_ASSETS",
    noteName: "Ställda säkerheter",
    legalReference: "K3 kap 8.21",
    requiresComparative: true,
    manualInputField: "previousYearPledgedAssets",
    helpText: "Ställda säkerheter vid föregående års utgång",
  },
  {
    noteId: "NOTE_CONTINGENT_LIABILITIES",
    noteName: "Eventualförpliktelser",
    legalReference: "ÅRL 5 kap 15§",
    requiresComparative: true,
    manualInputField: "previousYearContingentLiabilities",
    helpText: "Eventualförpliktelser vid föregående års utgång",
  },
];

export function K3ComparativeNotesSection({
  report,
  userInput,
  previousYearUserInput,
  onChange,
}: K3ComparativeNotesSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [comparatives, setComparatives] = useState<Record<string, number | null>>(
    userInput.comparativeNotes || {}
  );

  // Extract current year values from parsed SIE data
  const rawSie = report.parsedSieData;
  const calculations = report.calculations;

  // Build the list of notes that need comparative values
  const notesWithComparatives: ComparativeNoteData[] = COMPARATIVE_NOTES_CONFIG.map((config) => {
    let currentYear: number | null = null;
    let previousYear: number | null = null;

    // Try to extract current year from calculations or SIE
    if (config.noteId === "NOTE_EMPLOYEES_K3") {
      currentYear = userInput.averageEmployees ?? null;
      previousYear = previousYearUserInput?.averageEmployees ?? comparatives.previousYearEmployees ?? null;
    } else if (config.noteId === "NOTE_REVENUE_BREAKDOWN") {
      currentYear = calculations?.keyFigures?.netSales ?? null;
      previousYear = comparatives.previousYearRevenue ?? null;
    } else if (config.noteId === "NOTE_FIXED_ASSETS_INTANGIBLE") {
      currentYear = rawSie?.balanceSheet?.assets?.fixedAssets?.intangible ?? null;
      previousYear = comparatives.previousYearIntangibleAssets ?? null;
    } else if (config.noteId === "NOTE_FIXED_ASSETS_BUILDINGS") {
      currentYear = rawSie?.balanceSheet?.assets?.fixedAssets?.buildings ?? null;
      previousYear = comparatives.previousYearBuildings ?? null;
    } else if (config.noteId === "NOTE_FIXED_ASSETS_MACHINERY") {
      currentYear = rawSie?.balanceSheet?.assets?.fixedAssets?.machinery ?? null;
      previousYear = comparatives.previousYearMachinery ?? null;
    } else if (config.noteId === "NOTE_INVENTORY") {
      currentYear = rawSie?.balanceSheet?.assets?.currentAssets?.inventory ?? null;
      previousYear = comparatives.previousYearInventory ?? null;
    } else if (config.noteId === "NOTE_UNTAXED_RESERVES") {
      currentYear = rawSie?.balanceSheet?.equityAndLiabilities?.untaxedReserves ?? null;
      previousYear = comparatives.previousYearUntaxedReserves ?? null;
    } else if (config.noteId === "NOTE_DEFERRED_TAX") {
      // Calculated from untaxed reserves * 20.6%
      const untaxed = rawSie?.balanceSheet?.equityAndLiabilities?.untaxedReserves ?? 0;
      currentYear = untaxed > 0 ? Math.round(untaxed * 0.206) : null;
      previousYear = comparatives.previousYearDeferredTax ?? null;
    } else if (config.noteId === "NOTE_LONG_TERM_LIABILITIES") {
      currentYear = rawSie?.balanceSheet?.equityAndLiabilities?.longTermLiabilities ?? null;
      previousYear = comparatives.previousYearLongTermLiabilities ?? null;
    } else if (config.noteId === "NOTE_PLEDGED_ASSETS") {
      currentYear = userInput.pledgedAssets?.reduce((sum, a) => sum + (a.amount || 0), 0) ?? null;
      previousYear = previousYearUserInput?.pledgedAssets?.reduce((sum, a) => sum + (a.amount || 0), 0) ?? comparatives.previousYearPledgedAssets ?? null;
    } else if (config.noteId === "NOTE_CONTINGENT_LIABILITIES") {
      currentYear = userInput.contingentLiabilities?.reduce((sum, c) => sum + (c.amount || 0), 0) ?? null;
      previousYear = previousYearUserInput?.contingentLiabilities?.reduce((sum, c) => sum + (c.amount || 0), 0) ?? comparatives.previousYearContingentLiabilities ?? null;
    }

    // Determine if this note should be shown (only if current year has data)
    const isRelevant = currentYear !== null && currentYear !== 0;

    return {
      noteId: config.noteId,
      noteName: config.noteName,
      legalReference: config.legalReference,
      currentYear: isRelevant ? currentYear : null,
      previousYear,
      requiresComparative: config.requiresComparative,
      hasComparative: previousYear !== null,
      userInputField: config.manualInputField,
      helpText: config.helpText,
    };
  }).filter((note) => note.currentYear !== null);

  // Calculate statistics
  const totalNotes = notesWithComparatives.length;
  const notesWithComparativesCount = notesWithComparatives.filter((n) => n.hasComparative).length;
  const missingComparatives = totalNotes - notesWithComparativesCount;

  // Check if previous year data is available from SIE
  const hasPreviousYearInSie = rawSie?.previousYearBalanceSheet !== undefined && rawSie.previousYearBalanceSheet !== null;

  // Update parent when comparatives change
  useEffect(() => {
    onChange(comparatives);
  }, [comparatives, onChange]);

  const handleComparativeChange = (field: string, value: number | null) => {
    setComparatives((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="card">
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full"
      >
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
            <History className="text-violet-600" size={20} />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900">Jämförelsetal för noter</h3>
            <p className="text-sm text-gray-500">K3 kap 3.10 - Obligatoriska jämförelsetal</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
            K3
          </span>
          {missingComparatives > 0 ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
              <AlertTriangle size={12} />
              {missingComparatives} saknas
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <CheckCircle size={12} />
              Komplett
            </span>
          )}
          {isExpanded ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
        </div>
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          {/* Info box */}
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
            <div className="flex gap-2">
              <Info size={16} className="text-violet-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-violet-800">
                <p className="font-medium">K3 kräver jämförelsetal i alla noter</p>
                <p className="mt-1">
                  Till skillnad från K2 är jämförelsetal obligatoriska i K3-noter (K3 kap 3.10).
                  {hasPreviousYearInSie
                    ? " Föregående års data har hämtats från SIE-filen."
                    : " Ange föregående års värden manuellt nedan."}
                </p>
              </div>
            </div>
          </div>

          {/* Notes table */}
          {notesWithComparatives.length > 0 ? (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Not
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Innevarande år
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Föregående år
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {notesWithComparatives.map((note) => (
                    <tr key={note.noteId} className={!note.hasComparative ? "bg-amber-50" : ""}>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{note.noteName}</div>
                        <div className="text-xs text-gray-500">{note.legalReference}</div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm font-medium text-gray-900">
                          {note.currentYear !== null
                            ? `${formatNumber(note.currentYear)} kr`
                            : "-"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {note.hasComparative ? (
                          <span className="text-sm text-gray-900">
                            {formatNumber(note.previousYear!)} kr
                          </span>
                        ) : note.userInputField ? (
                          <div className="flex items-center justify-end gap-2">
                            <input
                              type="number"
                              value={comparatives[note.userInputField] ?? ""}
                              onChange={(e) =>
                                handleComparativeChange(
                                  note.userInputField!,
                                  e.target.value ? Number(e.target.value) : null
                                )
                              }
                              className="input w-28 text-right text-sm py-1"
                              placeholder="0"
                            />
                            <span className="text-xs text-gray-500">kr</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400 italic">Saknas</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {note.hasComparative || (note.userInputField && comparatives[note.userInputField]) ? (
                          <CheckCircle size={16} className="text-green-500 inline" />
                        ) : (
                          <AlertTriangle size={16} className="text-amber-500 inline" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>Inga noter kräver jämförelsetal baserat på aktuell data.</p>
            </div>
          )}

          {/* Summary */}
          {missingComparatives > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex gap-2">
                <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">
                    {missingComparatives} av {totalNotes} noter saknar jämförelsetal
                  </p>
                  <p className="mt-1">
                    Fyll i föregående års värden för fullständig K3-efterlevnad.
                    Om detta är första räkenskapsåret kan vissa jämförelsetal utelämnas.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Help text for first year */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500">
              <strong>Tips:</strong> Om detta är företagets första räkenskapsår enligt K3, eller om
              företaget byter från K2 till K3, kan jämförelsetal utelämnas för det första året.
              Ange värdet 0 om posten inte existerade föregående år.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
